import { yupResolver } from '@hookform/resolvers/yup'
import axios, { AxiosResponse } from 'axios'
import { Button, Input } from 'components/atomic'
import { useMemo, useState } from 'react'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { LoadingSpinner } from 'components/delayed'
import Filter from 'badwords-filter'
import _filter from 'lodash/filter'
import FilterEnSet from 'badwords-filter/filtersets/en.json'
import FilterDeadropSet from 'constants/eap-callsign-badwords'

const formSchema = yup.object().shape({
  gamerTag: yup
    .string()
    .trim()
    .required('GamerTag is required, not counting * and space characters')
    .min(4, 'Your gamertag must be between 4-15 characters long')
    .matches(/^[a-zA-Z0-9_\.-]+$/, { excludeEmptyString: true }) // TODO: VSM-649 comment out this line when game client / Pragma can accept unicode characters safely again
    .max(15, 'Your gamertag must be between 4-15 characters long'),
})

interface formData extends HTMLFormControlsCollection {
  gamerTag: string
}

interface EditGamerTagProps {
  isSmallScreen: boolean
  gamerTag: string
  setGamerTag: (gameName: string) => void
}

const EditGamerTag: React.FunctionComponent<EditGamerTagProps> = ({ isSmallScreen, gamerTag, setGamerTag }) => {
  const [serverError, setServerError] = useState('')
  const [completed, setCompleted] = useState(false)
  const [helperText, setHelperText] = useState('Gamertag can only be changed every 3 months. Spaces not allowed')
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm({
    defaultValues: useMemo(() => {
      return { gamerTag: gamerTag ?? '' }
    }, [gamerTag]),
    resolver: yupResolver(formSchema),
  })
  const formValues = useWatch({ control })
  const [isLoading, setIsLoading] = useState(false)
  let apiResponse: AxiosResponse<any, any>

  useEffect(() => {
    setServerError('')
    setCompleted(false)
  }, [formValues])

  // Set email detail
  const setGameName = async (gamerName: string) => {
    try {
      apiResponse = await axios.get(`/api/update-game-name?game_name=${gamerName}`)
      return { status: 1 }
    } catch (error) {
      if (error.response) {
        if (error.response.data.error) {
          setError('gamerTag', { type: 'custom', message: error.response.data.error })
        } else {
          const errUniq = _filter(error.response.data.response.errors, function (o) {
            return o.extensions.code === 'constraint-violation'
          })
          if (errUniq.length > 0) {
            setError('gamerTag', { type: 'custom', message: 'This gamertag has been taken, choose another' })
          }
        }
      } else {
        setServerError(error.message)
      }
      return { status: 0 }
    }
  }

  const onSubmit = async (data: formData) => {
    let gameName = data.gamerTag

    // Submit update user
    const badwordConfig = {
      list: [...FilterEnSet.filter, ...FilterDeadropSet.filter.map((word) => word.toLowerCase())],
      cleanWith: '*',
      useRegex: false,
    }
    const filterGameName = new Filter(badwordConfig)
    if (filterGameName.isUnclean(data.gamerTag)) {
      const gameNameCleaner = filterGameName.clean(data.gamerTag)
      gameName = filterGameName.normalize(gameNameCleaner)
      try {
        setValue('gamerTag', gameNameCleaner)
        await formSchema.validate({ gamerTag: gameName })
      } catch (err) {
        setError('gamerTag', { type: 'validate', message: err.errors[0] })
      }
      return
    }
    gameName = gameName.replace(/\s+/g, '')

    setIsLoading(true)
    const res = await setGameName(gameName)
    if (res.status === 1) {
      setCompleted(true)
      setGamerTag(gameName)
      setHelperText(apiResponse?.data.feedback || 'Gamertag set')
    }

    setIsLoading(false)
  }

  // Render UI
  return (
    <>
      <div className={`${isSmallScreen ? 'sm:mt-5 md:mt-0 lg:mt-0 lg:border' : 'border'} mb-8 border-[#454340] px-10`}>
        <LoadingSpinner isShown={isLoading} className="bg-black/90" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`${isSmallScreen ? 'lg:flex' : 'flex items-center'} py-9`}>
            <div className="mr-5">
              <Input
                {...register('gamerTag', { required: true })}
                rtlDirection={true}
                type="text"
                label="Gamertag"
                placeholder="Enter GamerTag"
                helper={helperText}
                error={errors.gamerTag?.message}
                success={completed}
              />
            </div>

            {/* Button submit */}
            <div className={`${isSmallScreen ? 'mt-3 lg:mt-0' : 'mr-2'}`}>
              <Button variant="primary" className="outline-none" state={isSubmitting ? 'disabled' : ''}>
                Submit
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default EditGamerTag
